/** Standard top-of-page header for admin screens: title (+ optional subtitle)
 *  on the left, optional meta text and action buttons on the right.
 *
 *  Every admin page used to hand-roll `flex items-center justify-between` here,
 *  which has no wrap: once the title block wrapped to 2-3 lines on a phone the
 *  right-hand column got squeezed to a sliver and its own labels broke mid-phrase
 *  ("2575" / "total" on separate lines). This stacks the two halves below `sm:`
 *  and only goes horizontal once there is room for it.
 *
 *  `children` renders inside the left block, under the subtitle — the escape
 *  hatch for pages that need an extra note/badge line that can't legally nest
 *  inside the subtitle's <p>.
 */
const AdminPageHeader: React.FC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  /** Appended to the wrapper. Passing any `mb-*` here replaces the default
   *  `mb-6` (for pages whose parent already spaces children via space-y/gap). */
  className?: string;
}> = ({ title, subtitle, meta, actions, children, className = "" }) => {
  const base = "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4";
  const margin = /(^|\s)-?mb-/.test(className) ? "" : " mb-6";
  const wrapper = `${base}${margin}${className ? ` ${className}` : ""}`;

  return (
    <div className={wrapper}>
      <div className="min-w-0">
        <h1 className="text-2xl font-bold text-white">{title}</h1>
        {subtitle ? <p className="text-gray-500 mt-1">{subtitle}</p> : null}
        {children}
      </div>
      {meta || actions ? (
        <div className="flex flex-wrap items-center gap-3 sm:shrink-0 sm:justify-end">
          {meta ? <span className="text-sm text-gray-500 whitespace-nowrap">{meta}</span> : null}
          {actions}
        </div>
      ) : null}
    </div>
  );
};

export default AdminPageHeader;
